import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Textarea,
} from '@marrafa-gioale/gymx-ui-web'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowLeft, CheckCircle2, CircleAlert, FileJson, FlaskConical, Upload } from 'lucide-react'
import { useMemo, useRef, useState } from 'react'

import { newUuid } from '@/api/client'
import { BULK_UPSERT_MAX_ITEMS } from '@/api/endpoints/exercises'
import { ApiError, errorMessage } from '@/api/errors'
import { useBulkUpsert } from '@/api/queries'
import type { BulkUpsertResult, ExerciseInput } from '@/api/types'
import { PageHeader } from '@/components/layout/app-shell'
import { formatNumber } from '@/lib/format'
import { notify } from '@/lib/toast'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/_app/esercizi/bulk')({
  component: BulkUpsertPage,
})

interface ParseResult {
  exercises: ExerciseInput[]
  error: string | null
}

/**
 * Analisi del JSON incollato.
 *
 * Accetta sia un array nudo sia `{ "exercises": [...] }`, perché sono le due forme in
 * cui il dataset gira già: il seed è un array, il corpo dell'endpoint ha la chiave.
 * Costringere l'operatore a riavvolgere il file a mano non aggiunge niente.
 */
function parsePayload(raw: string): ParseResult {
  const trimmed = raw.trim()
  if (!trimmed) return { exercises: [], error: null }

  let parsed: unknown
  try {
    parsed = JSON.parse(trimmed)
  } catch (cause) {
    return {
      exercises: [],
      error: `JSON non valido: ${cause instanceof Error ? cause.message : 'formato illeggibile'}`,
    }
  }

  const list = Array.isArray(parsed)
    ? parsed
    : typeof parsed === 'object' &&
        parsed !== null &&
        Array.isArray((parsed as { exercises?: unknown[] }).exercises)
      ? (parsed as { exercises: unknown[] }).exercises
      : null

  if (!list) {
    return {
      exercises: [],
      error: 'Serve un array di esercizi, oppure un oggetto con la chiave "exercises".',
    }
  }

  if (list.length === 0) return { exercises: [], error: 'L’elenco è vuoto.' }

  if (list.length > BULK_UPSERT_MAX_ITEMS) {
    return {
      exercises: [],
      error: `Massimo ${BULK_UPSERT_MAX_ITEMS} esercizi per richiesta: ne hai ${formatNumber(list.length)}. Dividi il file.`,
    }
  }

  // La validazione riga per riga è del server, che risponde con indice e motivo per
  // ciascuna riga rifiutata. Qui si controlla solo che siano oggetti: un array di
  // stringhe è un errore di file, non di dati, e vale la pena dirlo subito.
  const nonObject = list.findIndex((item) => typeof item !== 'object' || item === null)
  if (nonObject !== -1) {
    return { exercises: [], error: `La riga ${nonObject} non è un oggetto.` }
  }

  return { exercises: list as ExerciseInput[], error: null }
}

function BulkUpsertPage() {
  const [raw, setRaw] = useState('')
  const [result, setResult] = useState<BulkUpsertResult | null>(null)
  const [dragging, setDragging] = useState(false)
  const fileInput = useRef<HTMLInputElement>(null)

  /**
   * Chiave di idempotenza stabile per questo caricamento.
   *
   * Rimane la stessa fra la prova e l'applicazione e su eventuali retry: se la
   * risposta si perde su una richiesta andata a buon fine, ripeterla restituisce
   * l'esito originale invece di riscrivere il batch.
   */
  const [idempotencyKey, setIdempotencyKey] = useState(() => newUuid())

  const bulk = useBulkUpsert()
  const parsed = useMemo(() => parsePayload(raw), [raw])
  const canSubmit = parsed.exercises.length > 0 && parsed.error === null

  const readFile = async (file: File) => {
    try {
      const text = await file.text()
      setRaw(text)
      setResult(null)
      setIdempotencyKey(newUuid())
      notify({ message: `${file.name} caricato.`, variant: 'success' })
    } catch {
      notify({ message: 'Impossibile leggere il file.', variant: 'danger' })
    }
  }

  const run = async (dryRun: boolean) => {
    if (!canSubmit) return
    try {
      const outcome = await bulk.mutateAsync({
        exercises: parsed.exercises,
        dryRun,
        idempotencyKey,
      })
      setResult(outcome)

      if (dryRun) {
        notify({ message: 'Prova completata: nulla è stato scritto.', variant: 'success' })
      } else {
        notify({
          message: `Caricamento applicato: ${formatNumber(outcome.created)} creati, ${formatNumber(outcome.updated)} aggiornati.`,
          variant: 'success',
        })
        // Un'applicazione riuscita chiude questo caricamento: la chiave non va
        // riusata per un batch diverso.
        setIdempotencyKey(newUuid())
      }
    } catch (error) {
      notify({
        // Il `requestId` va in coda al messaggio, non in una seconda riga: è ciò
        // che rende la segnalazione correlabile ai log (docs/02-convenzioni.md).
        message:
          error instanceof ApiError && error.requestId
            ? `${errorMessage(error)} (requestId: ${error.requestId})`
            : errorMessage(error),
        variant: 'danger',
      })
    }
  }

  return (
    <>
      <PageHeader
        title="Caricamento in blocco"
        description="Sostituisce il caricamento del catalogo con uno script fuori banda, che richiedeva di allentare i permessi di scrittura per la sua durata. Qui l'autorizzazione è per ruolo, e la scrittura è tracciata."
        actions={
          <Button variant="ghost" asChild>
            <Link to="/esercizi">
              <ArrowLeft />
              Torna al catalogo
            </Link>
          </Button>
        }
      />

      <div className="grid gap-4 lg:grid-cols-[1fr_22rem]">
        <Card>
          <CardHeader>
            <div className="space-y-1">
              <CardTitle>Esercizi da caricare</CardTitle>
              <CardDescription>
                Array JSON o oggetto con chiave <code className="font-mono">exercises</code>.
                Massimo {BULK_UPSERT_MAX_ITEMS} righe per richiesta.
              </CardDescription>
            </div>
            <Button size="sm" variant="outline" onClick={() => fileInput.current?.click()}>
              <FileJson />
              Scegli file
            </Button>
          </CardHeader>

          <CardContent className="space-y-3">
            {/* Il controllo reale è il bottone «Scegli file»: questo input esiste solo
                per aprire il selettore. `sr-only` lo lascerebbe focalizzabile ma
                invisibile e senza nome, quindi va escluso da tastiera e da screen
                reader — `hidden` non basta su Safari, che ignora il click. */}
            <input
              ref={fileInput}
              type="file"
              accept="application/json,.json"
              tabIndex={-1}
              aria-hidden
              className="sr-only"
              onChange={(event) => {
                const file = event.target.files?.[0]
                if (file) void readFile(file)
                event.target.value = ''
              }}
            />

            <div
              onDragOver={(event) => {
                event.preventDefault()
                setDragging(true)
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(event) => {
                event.preventDefault()
                setDragging(false)
                const file = event.dataTransfer.files[0]
                if (file) void readFile(file)
              }}
              className={cn(
                // L'anello di focus vive sul contenitore: la textarea dentro ha bordo
                // e anello azzerati per fondersi con l'area di rilascio, e senza
                // questo `focus-within` la navigazione da tastiera non avrebbe alcun
                // riferimento visivo.
                'rounded-md border border-dashed transition-colors',
                'focus-within:border-ring focus-within:ring-ring/30 focus-within:ring-2',
                dragging ? 'border-ring bg-primary-soft' : 'border-border',
              )}
            >
              <Textarea
                value={raw}
                onChange={(event) => {
                  setRaw(event.target.value)
                  setResult(null)
                }}
                rows={16}
                spellCheck={false}
                aria-label="Esercizi in formato JSON"
                className="resize-y border-0 bg-transparent font-mono text-base focus-visible:ring-0 sm:text-xs"
                placeholder={
                  dragging
                    ? 'Rilascia il file qui'
                    : '[\n  {\n    "id": "man_panca_piana",\n    "name": "Panca piana",\n    "primaryMuscles": ["chest"],\n    "instructions": ["Sdraiati sulla panca…"],\n    "images": ["https://…"],\n    "published": false\n  }\n]'
                }
              />
            </div>

            {parsed.error ? (
              <Alert variant="danger" title="File non utilizzabile">
                {parsed.error}
              </Alert>
            ) : parsed.exercises.length > 0 ? (
              <p className="text-muted-foreground text-sm">
                <span className="text-foreground font-medium" data-numeric>
                  {formatNumber(parsed.exercises.length)}
                </span>{' '}
                {parsed.exercises.length === 1 ? 'esercizio' : 'esercizi'} pronti.
              </p>
            ) : null}

            <div className="flex flex-col gap-2 sm:flex-row">
              <Button
                variant="outline"
                disabled={!canSubmit}
                loading={bulk.isPending && bulk.variables?.dryRun === true}
                onClick={() => void run(true)}
              >
                <FlaskConical />
                Prova senza scrivere
              </Button>
              <Button
                variant="primary"
                disabled={!canSubmit}
                loading={bulk.isPending && bulk.variables?.dryRun === false}
                onClick={() => void run(false)}
              >
                <Upload />
                Applica
              </Button>
            </div>

            <p className="text-muted-foreground text-xs">
              L'operazione non è atomica: le righe valide vengono scritte anche se altre falliscono.
              Un record malformato non blocca un caricamento di centinaia di esercizi.
            </p>
          </CardContent>
        </Card>

        <div className="space-y-4">{result ? <ResultCard result={result} /> : <HintCard />}</div>
      </div>
    </>
  )
}

function HintCard() {
  return (
    <Card>
      <CardHeader>
        <div className="space-y-1">
          <CardTitle>Prima di applicare</CardTitle>
          <CardDescription>Tre cose che vale la pena sapere.</CardDescription>
        </div>
      </CardHeader>
      <CardContent>
        <ul className="text-muted-foreground space-y-3 text-sm">
          <li>
            <span className="text-foreground font-medium">Fai una prova.</span> Con la prova il
            server valida tutto e riporta il piano senza scrivere niente.
          </li>
          <li>
            <span className="text-foreground font-medium">L'id identifica la riga.</span> Con un id
            già in catalogo la riga viene aggiornata; senza id il server ne genera uno nuovo, e
            ripetere il file creerebbe duplicati.
          </li>
          <li>
            <span className="text-foreground font-medium">
              Carica non pubblicato se i dati sono incompleti.
            </span>{' '}
            Con <code className="font-mono text-xs">"published": false</code> il batch si rivede e
            si pubblica dopo, invece di comparire subito nell'app.
          </li>
        </ul>
      </CardContent>
    </Card>
  )
}

function ResultCard({ result }: { result: BulkUpsertResult }) {
  const errors = result.errors ?? []

  return (
    <Card>
      <CardHeader>
        <div className="space-y-1">
          <CardTitle>{result.dryRun ? 'Esito della prova' : 'Esito del caricamento'}</CardTitle>
          <CardDescription>
            {result.dryRun
              ? 'Nulla è stato scritto sul catalogo.'
              : 'Il catalogo è stato aggiornato.'}
          </CardDescription>
        </div>
        {result.dryRun ? (
          <Badge variant="secondary">
            <FlaskConical />
            Prova
          </Badge>
        ) : (
          <Badge variant="success">
            <CheckCircle2 />
            Applicato
          </Badge>
        )}
      </CardHeader>

      <CardContent className="space-y-4">
        <dl className="grid grid-cols-2 gap-3">
          <Metric label="Creati" value={result.created} tone="success" />
          <Metric label="Aggiornati" value={result.updated} tone="secondary" />
          <Metric label="Invariati" value={result.unchanged} />
          <Metric
            label="Rifiutati"
            value={result.failed}
            tone={result.failed > 0 ? 'danger' : undefined}
          />
        </dl>

        {result.catalogVersionAfter ? (
          <p className="text-muted-foreground text-xs">
            Versione catalogo: <code className="font-mono">{result.catalogVersionAfter}</code>
          </p>
        ) : null}

        {errors.length > 0 ? (
          <div className="border-border space-y-2 border-t pt-4">
            <p className="flex items-center gap-1.5 text-sm font-medium">
              <CircleAlert className="text-danger-content size-4" aria-hidden />
              Righe rifiutate
            </p>
            <ul className="max-h-72 scrollbar-thin space-y-2 overflow-y-auto">
              {errors.map((error) => (
                <li
                  key={`${error.index}-${error.code}`}
                  className="border-danger/30 bg-danger-soft rounded-sm border p-2 text-xs"
                >
                  <p className="font-medium">
                    Riga {error.index}
                    {error.id ? <span className="font-mono font-normal"> · {error.id}</span> : null}
                  </p>
                  <p className="text-muted-foreground mt-0.5">{error.message}</p>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {result.dryRun && result.failed === 0 ? (
          <Alert variant="success" title="Nessun errore">
            Il file è valido: puoi applicarlo.
          </Alert>
        ) : null}
      </CardContent>
    </Card>
  )
}

function Metric({
  label,
  value,
  tone,
}: {
  label: string
  value: number
  tone?: 'success' | 'secondary' | 'danger'
}) {
  const color = {
    success: 'text-success-content',
    secondary: 'text-secondary-content',
    danger: 'text-danger-content',
  }

  return (
    <div className="border-border bg-surface-alt rounded-md border p-3">
      <dt className="text-muted-foreground text-xs">{label}</dt>
      <dd
        className={cn('mt-0.5 text-xl leading-none font-semibold', tone ? color[tone] : undefined)}
      >
        {formatNumber(value)}
      </dd>
    </div>
  )
}
